import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { Mentorship, MentorshipStatus, User } from '../types';

const MENTORSHIPS = 'mentorships';

/** Thrown for rule violations the UI is expected to show as a message. */
export class MentorshipError extends Error {}

export function parseMentorshipDoc(id: string, data: any): Mentorship {
  return {
    id,
    mentorUserId: String(data.mentorUserId || ''),
    mentorName: String(data.mentorName || ''),
    apprenticeUserId: String(data.apprenticeUserId || ''),
    apprenticeName: String(data.apprenticeName || ''),
    status: data.status === 'completed' ? 'completed' : 'active',
    caseIds: Array.isArray(data.caseIds)
      ? data.caseIds.filter((value: unknown): value is string => typeof value === 'string')
      : [],
    startedAt: data.startedAt?.toDate?.() ?? new Date(0),
    completedAt: data.completedAt?.toDate?.() ?? null,
    createdBy: String(data.createdBy || ''),
    createdByName: String(data.createdByName || ''),
    updatedAt: data.updatedAt?.toDate?.() ?? new Date(0),
  };
}

export async function fetchAllMentorships(): Promise<Mentorship[]> {
  const snap = await getDocs(collection(db, MENTORSHIPS));
  return snap.docs.map((docSnap) => parseMentorshipDoc(docSnap.id, docSnap.data()));
}

/** Every pairing this user takes part in, as mentor or as apprentice. */
export async function fetchMentorshipsForUser(userId: string): Promise<Mentorship[]> {
  const ref = collection(db, MENTORSHIPS);
  const [asApprentice, asMentor] = await Promise.all([
    getDocs(query(ref, where('apprenticeUserId', '==', userId))),
    getDocs(query(ref, where('mentorUserId', '==', userId))),
  ]);

  const byId = new Map<string, Mentorship>();
  [...asApprentice.docs, ...asMentor.docs].forEach((docSnap) => {
    byId.set(docSnap.id, parseMentorshipDoc(docSnap.id, docSnap.data()));
  });
  return Array.from(byId.values());
}

// ---------------------------------------------------------------------------
// Access resolution
// ---------------------------------------------------------------------------

/**
 * 'active'   — may read the case and write their own notes on it.
 * 'readonly' — mentorship finished: still visible, nothing writable.
 * null       — no apprentice grant for this case.
 */
export type ApprenticeAccess = 'active' | 'readonly' | null;

/**
 * An active pairing wins over a completed one, so re-pairing an apprentice on a
 * case they observed before restores write access instead of staying read-only.
 */
export function resolveApprenticeAccess(
  caseId: string,
  apprenticeUserId: string,
  mentorships: Mentorship[]
): ApprenticeAccess {
  let readonly = false;
  for (const mentorship of mentorships) {
    if (mentorship.apprenticeUserId !== apprenticeUserId) continue;
    if (!mentorship.caseIds.includes(caseId)) continue;
    if (mentorship.status === 'active') return 'active';
    readonly = true;
  }
  return readonly ? 'readonly' : null;
}

export interface ApprenticeCaseSets {
  /** Every case visible to the user through a pairing, whatever its status. */
  all: Set<string>;
  /** Subset the user may still add or edit their own notes on. */
  writable: Set<string>;
}

export function buildApprenticeCaseSets(
  apprenticeUserId: string,
  mentorships: Mentorship[]
): ApprenticeCaseSets {
  const all = new Set<string>();
  const writable = new Set<string>();

  mentorships.forEach((mentorship) => {
    if (mentorship.apprenticeUserId !== apprenticeUserId) return;
    mentorship.caseIds.forEach((caseId) => {
      all.add(caseId);
      if (mentorship.status === 'active') writable.add(caseId);
    });
  });

  return { all, writable };
}

/** Apprentices attached to one of the mentor's cases, for the owner-side badge. */
export function apprenticesForCase(
  caseId: string,
  mentorships: Mentorship[]
): Array<{ userId: string; name: string; status: MentorshipStatus }> {
  return mentorships
    .filter((mentorship) => mentorship.caseIds.includes(caseId))
    .map((mentorship) => ({
      userId: mentorship.apprenticeUserId,
      name: mentorship.apprenticeName,
      status: mentorship.status,
    }));
}

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

export function activeMentorshipsForMentor(
  mentorUserId: string,
  mentorships: Mentorship[]
): Mentorship[] {
  return mentorships.filter(
    (m) => m.mentorUserId === mentorUserId && m.status === 'active'
  );
}

export function activeMentorshipForApprentice(
  apprenticeUserId: string,
  mentorships: Mentorship[]
): Mentorship | null {
  return (
    mentorships.find(
      (m) => m.apprenticeUserId === apprenticeUserId && m.status === 'active'
    ) ?? null
  );
}

/** A user may be a mentor or an apprentice, never both at the same time. */
export function isActiveApprentice(userId: string, mentorships: Mentorship[]): boolean {
  return activeMentorshipForApprentice(userId, mentorships) !== null;
}

// ---------------------------------------------------------------------------
// Writes — all of these assume the caller is the supreme leader
// ---------------------------------------------------------------------------

export async function setUserAsMentor(userId: string): Promise<void> {
  await updateDoc(doc(db, 'users', userId), { isMentor: true });
}

/**
 * Refuses while the mentor still has active apprentices: the pairing has to be
 * completed explicitly, so nobody loses access as a side effect.
 */
export async function unsetUserAsMentor(
  userId: string,
  mentorships: Mentorship[]
): Promise<void> {
  const active = activeMentorshipsForMentor(userId, mentorships);
  if (active.length > 0) {
    throw new MentorshipError(
      active.map((m) => m.apprenticeName).join(', ')
    );
  }
  await updateDoc(doc(db, 'users', userId), { isMentor: false });
}

export async function createMentorship(input: {
  mentor: User;
  apprentice: User;
  createdBy: string;
  createdByName: string;
}): Promise<void> {
  await addDoc(collection(db, MENTORSHIPS), {
    mentorUserId: input.mentor.id,
    mentorName: input.mentor.fullName,
    apprenticeUserId: input.apprentice.id,
    apprenticeName: input.apprentice.fullName,
    status: 'active' as MentorshipStatus,
    caseIds: [],
    startedAt: serverTimestamp(),
    completedAt: null,
    createdBy: input.createdBy,
    createdByName: input.createdByName,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Rewrites the whole grant list. Sending the full array (instead of
 * arrayUnion/arrayRemove) keeps the dialog's checkbox state authoritative.
 */
export async function setMentorshipCases(
  mentorshipId: string,
  caseIds: string[]
): Promise<void> {
  await updateDoc(doc(db, MENTORSHIPS, mentorshipId), {
    caseIds: Array.from(new Set(caseIds)),
    updatedAt: serverTimestamp(),
  });
}

/** Keeps the record and the granted cases; only drops write access. */
export async function completeMentorship(mentorshipId: string): Promise<void> {
  await updateDoc(doc(db, MENTORSHIPS, mentorshipId), {
    status: 'completed' as MentorshipStatus,
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Removes the pairing outright — for one created by mistake. Notes the
 * apprentice already wrote stay on the case: they are their own documents,
 * authored by them, and deleting a pairing must not rewrite case history.
 */
export async function deleteMentorship(mentorshipId: string): Promise<void> {
  await deleteDoc(doc(db, MENTORSHIPS, mentorshipId));
}

export async function reopenMentorship(mentorshipId: string): Promise<void> {
  await updateDoc(doc(db, MENTORSHIPS, mentorshipId), {
    status: 'active' as MentorshipStatus,
    completedAt: null,
    updatedAt: serverTimestamp(),
  });
}
